/* The state and actions behind a solids recap's Contract block, shared by the
 * classic block (components/Legal/RecapContractBlock.js) and the new look one
 * (newlook/pages/traderecap/solids/RecapContractBlock.js), so the two looks
 * can only differ in markup.
 *
 * ``initialSummary`` is the recap's ``legal_contract_summary`` (null when the
 * leg has no contract yet). The recap screen does not re-read the recap after
 * a save, so the block keeps its own copy and replaces it from each action's
 * response (``summary``). ``onContractNumber`` hands a new C-number to the
 * recap form so its Contract No. field shows it and a later recap save sends
 * it back unchanged instead of the blank it loaded with.
 */
import { useCallback, useEffect, useState } from 'react';

import legalContractService, { legalRefusal } from '../../services/legalContractService';

export default function useRecapContract({ recapId, initialSummary, onContractNumber }) {
  const [contract, setContract] = useState(initialSummary || null);
  // 'create' | 'status' | 'send' | file id (download) while a request runs.
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // The recap loads after the first render; take its contract when it lands.
  useEffect(() => { setContract(initialSummary || null); }, [initialSummary]);

  const run = useCallback(async (key, call, fallback, success) => {
    setBusy(key);
    setError('');
    setNotice('');
    try {
      const data = await call();
      setContract(data.summary);
      if (success) setNotice(success(data.summary));
      return data.summary;
    } catch (err) {
      setError(legalRefusal(err, fallback));
      return null;
    } finally {
      setBusy(null);
    }
  }, []);

  const create = useCallback(async (mode, file) => {
    const summary = await run(
      'create',
      () => legalContractService.createFromRecap(recapId, mode, file),
      'Could not create the contract. Please try again.',
      (s) => `Contract ${s.contract_number} created.`,
    );
    if (summary && onContractNumber) onContractNumber(summary.contract_number);
    return summary;
  }, [recapId, run, onContractNumber]);

  const changeStatus = useCallback((status) => run(
    'status',
    () => legalContractService.setStatus(contract.id, status),
    'Could not change the contract status. Please try again.',
    (s) => `Contract status is now ${s.status_display}.`,
  ), [contract, run]);

  const sendToLegal = useCallback(() => run(
    'send',
    () => legalContractService.sendToLegal(contract.id),
    'Could not send the contract to Legal. Please try again.',
    () => 'Sent to Legal for review. Legal has been notified.',
  ), [contract, run]);

  const download = useCallback(async (file) => {
    setBusy(file.id);
    setError('');
    try {
      const blob = await legalContractService.downloadFile(contract.id, file.id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = file.original_filename || `${contract.contract_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Could not download that file. Please try again.');
    } finally {
      setBusy(null);
    }
  }, [contract]);

  return {
    contract, busy, error, notice, create, changeStatus, sendToLegal, download,
    clearError: () => setError(''),
  };
}

// The Legal review line both looks print under the status.
export const reviewLine = (contract) => {
  if (!contract) return '';
  if (contract.legal_review_status === 'pending') return 'With Legal for review';
  if (contract.legal_review_status === 'reviewed') {
    return contract.review_note
      ? `Reviewed by Legal: ${contract.review_note}`
      : 'Reviewed by Legal';
  }
  return 'Not sent to Legal';
};

// Allowed file types for an external contract, as the server checks them.
export const CONTRACT_FILE_ACCEPT = '.pdf,.doc,.docx,.png,.jpg,.jpeg,.txt';
